import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, CheckCircle, Gamepad2, TrendingUp, Clock, Award, Zap, Target } from 'lucide-react';
import { Helmet } from 'react-helmet';

const RSEInteractiveGuideBlog = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>RSE Interactive Quick Study Guide 2025 | Master Your Retail Securities Exam</title>
        <meta name="description" content="Master the Retail Securities Exam (RSE) with our interactive study guide. 9 elements, games, quizzes, flashcards, and full mock exam. Pass your CIRO RSE exam on the first try." />
        <meta name="keywords" content="RSE exam, Retail Securities Exam, CIRO RSE prep, retail securities exam Canada, investment advisor exam, interactive RSE study guide, CIRO course, financial advisor exam Canada, RSE study materials" />
        <meta property="og:title" content="RSE Interactive Quick Study Guide | Master the Retail Securities Exam" />
        <meta property="og:description" content="Master all 9 elements with interactive games, quizzes, and element-wise readings. $49.99 CAD (Regular: $129.99) one-time payment." />
        <meta property="og:type" content="article" />
        <link rel="canonical" href="https://vittafinance.ca/blog/rse-interactive-study-guide" />
      </Helmet>

      <article style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)'
      }}>
        {/* Hero Section */}
        <div style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          padding: '80px 20px 60px',
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(255,255,255,0.2)',
              padding: '8px 20px',
              borderRadius: '100px',
              marginBottom: '24px',
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '0.5px'
            }}>
              RSE EXAM PREPARATION 2025
            </div>
            <h1 style={{
              fontSize: 'clamp(32px, 5vw, 52px)',
              fontWeight: 900,
              marginBottom: '24px',
              lineHeight: 1.2
            }}>
              Master the RSE Exam with Canada's Interactive Study Guide
            </h1>
            <p style={{
              fontSize: '20px',
              opacity: 0.95,
              maxWidth: '700px',
              margin: '0 auto 32px',
              lineHeight: 1.6
            }}>
              Pass your Retail Securities Examination on the first try with 9 interactive elements, gamified learning, and a comprehensive mock exam
            </p>
            <div style={{
              display: 'flex',
              gap: '20px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <Link
                to="/books/rse-interactive"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '16px 32px',
                  background: '#D4AF37',
                  color: 'white',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  fontSize: '18px',
                  fontWeight: 700,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                }}
              >
                <BookOpen size={22} />
                Get Instant Access - $49.99 CAD (Regular: $129.99)
              </Link>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: '60px 20px'
        }}>
          {/* Introduction */}
          <section style={{ marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '20px'
            }}>
              What is the RSE Exam?
            </h2>
            <p style={{
              fontSize: '18px',
              lineHeight: 1.8,
              color: '#475569',
              marginBottom: '16px'
            }}>
              The <strong>Retail Securities Exam (RSE)</strong> is a mandatory licensing exam for professionals seeking to advise retail clients in Canada's investment industry. Administered by the <strong>Canadian Investment Regulatory Organization (CIRO)</strong>, this comprehensive exam tests your knowledge across securities products, client relationships, trading practices, and regulatory compliance.
            </p>
            <p style={{
              fontSize: '18px',
              lineHeight: 1.8,
              color: '#475569',
              marginBottom: '16px'
            }}>
              Whether you're pursuing a career as an <strong>Investment Advisor</strong>, <strong>Registered Representative</strong>, or entering the retail securities industry in Ontario, British Columbia, Alberta, Quebec, or anywhere across Canada, the RSE is your gateway to becoming CIRO-licensed and serving individual investors.
            </p>
          </section>

          {/* Why This Guide? */}
          <section style={{ marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '24px'
            }}>
              Why Choose the RSE Interactive Quick Study Guide?
            </h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '24px',
              marginBottom: '32px'
            }}>
              {[
                { icon: <Gamepad2 size={32} />, color: '#3b82f6', title: 'Interactive Games', desc: '40+ games across all elements to reinforce learning through play' },
                { icon: <Target size={32} />, color: '#10b981', title: 'Full-Length Mock Exam', desc: 'Complete practice exam with detailed diagnostic report' },
                { icon: <Clock size={32} />, color: '#f59e0b', title: 'Element-Wise Learning', desc: 'Structured chapters covering all 9 CIRO RSE elements systematically' },
                { icon: <Award size={32} />, color: '#8b5cf6', title: 'Exam Readiness Gauge', desc: 'Track your performance against the pass threshold' },
              ].map((feature, idx) => (
                <div key={idx} style={{
                  padding: '28px',
                  background: 'white',
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <div style={{ color: feature.color, marginBottom: '16px' }}>
                    {feature.icon}
                  </div>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#1e293b',
                    marginBottom: '8px'
                  }}>
                    {feature.title}
                  </h3>
                  <p style={{
                    fontSize: '15px',
                    lineHeight: 1.6,
                    color: '#64748b'
                  }}>
                    {feature.desc}
                  </p>
                </div>
              ))}
            </div>

            <div style={{
              background: '#f0f9ff',
              border: '2px solid #3b82f6',
              borderRadius: '12px',
              padding: '28px',
              marginTop: '32px'
            }}>
              <h3 style={{
                fontSize: '22px',
                fontWeight: 700,
                color: '#1e40af',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <Zap size={24} />
                Perfect for Aspiring Retail Investment Advisors
              </h3>
              <p style={{
                fontSize: '16px',
                lineHeight: 1.7,
                color: '#334155',
                marginBottom: '12px'
              }}>
                This guide is specifically designed for candidates across <strong>Canada</strong> preparing for the RSE exam in 2025. Whether you're in Toronto, Vancouver, Calgary, Montreal, or anywhere across the provinces, this is your complete exam prep solution.
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  'One-time payment of $49.99 CAD (Regular: $129.99)',
                  'Lifetime access to all updates',
                  'Mobile-friendly for studying anywhere',
                  'No recurring fees or hidden costs'
                ].map((point, idx) => (
                  <li key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '8px 0',
                    fontSize: '15px',
                    color: '#334155'
                  }}>
                    <CheckCircle size={20} color="#10b981" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* All 9 Elements Breakdown */}
          <section style={{ marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '24px'
            }}>
              Complete Coverage: All 9 RSE Elements
            </h2>
            <p style={{
              fontSize: '18px',
              lineHeight: 1.8,
              color: '#475569',
              marginBottom: '32px'
            }}>
              Each element includes <strong>interactive games</strong>, <strong>flashcards</strong>, and <strong>exam-style quizzes</strong> to ensure you master the material:
            </p>

            <div style={{
              display: 'grid',
              gap: '20px'
            }}>
              {[
                { num: 1, icon: '📘', title: 'Canadian Regulatory Environment', desc: 'Master CIRO, provincial regulators, CIPF, FINTRAC, registration requirements, and the Canadian securities regulatory framework' },
                { num: 2, icon: '📗', title: 'Economic Analysis & Markets', desc: 'Economic indicators, monetary policy, business cycles, industry analysis, and market fundamentals for retail securities' },
                { num: 3, icon: '📙', title: 'Financial Statements Analysis', desc: 'Income statements, balance sheets, cash flow analysis, financial ratios, and corporate financial health assessment' },
                { num: 4, icon: '📕', title: 'Equity Securities', desc: 'Common stock, preferred shares, stock valuation, dividend analysis, equity markets, and trading mechanics' },
                { num: 5, icon: '📓', title: 'Fixed Income Securities', desc: 'Bonds, debentures, GICs, T-bills, yield calculations, duration, convexity, and fixed income trading' },
                { num: 6, icon: '📔', title: 'Managed Products', desc: 'Mutual funds, ETFs, segregated funds, hedge funds, fee structures, fund analysis, and suitability assessment' },
                { num: 7, icon: '📒', title: 'Derivatives & Options', desc: 'Options, futures, hedging strategies, speculation, risk profiles, and derivative regulations for retail clients' },
                { num: 8, icon: '📚', title: 'Client Relationships & KYC', desc: 'Know Your Client obligations, suitability assessment, account types, documentation requirements, and CRM framework' },
                { num: 9, icon: '🎓', title: 'Ethics & Professional Conduct', desc: 'CIRO ethical standards, conflicts of interest, personal conduct rules, confidentiality, and compliance obligations' },
              ].map((element) => (
                <div key={element.num} style={{
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderLeft: '4px solid #3b82f6',
                  borderRadius: '12px',
                  padding: '24px',
                  transition: 'all 0.2s'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px'
                  }}>
                    <span style={{ fontSize: '40px' }}>{element.icon}</span>
                    <div>
                      <h3 style={{
                        fontSize: '20px',
                        fontWeight: 700,
                        color: '#1e293b',
                        marginBottom: '8px'
                      }}>
                        Element {element.num}: {element.title}
                      </h3>
                      <p style={{
                        fontSize: '15px',
                        lineHeight: 1.7,
                        color: '#64748b'
                      }}>
                        {element.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Mock Exam Feature */}
          <section style={{ marginBottom: '60px' }}>
            <div style={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              borderRadius: '16px',
              padding: '40px',
              border: '2px solid #f59e0b'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '48px' }}>🎯</span>
                <h2 style={{
                  fontSize: '28px',
                  fontWeight: 800,
                  color: '#78350f',
                  margin: 0
                }}>
                  Full-Length RSE Mock Exam
                </h2>
              </div>
              <p style={{
                fontSize: '16px',
                lineHeight: 1.7,
                color: '#78350f',
                marginBottom: '20px'
              }}>
                Test your exam readiness with a complete mock exam featuring:
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px'
              }}>
                {[
                  'Exam Readiness Gauge',
                  'Performance Tier Analysis',
                  'Element-by-Element Breakdown',
                  'Targeted Study Recommendations',
                  'Time Management Analytics',
                  'Missed Questions Review'
                ].map((feature, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: '#78350f',
                    fontWeight: 600
                  }}>
                    <CheckCircle size={18} color="#f59e0b" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* RSE Career Path */}
          <section style={{ marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '24px'
            }}>
              Your Career as a Retail Investment Advisor
            </h2>
            <p style={{
              fontSize: '18px',
              lineHeight: 1.8,
              color: '#475569',
              marginBottom: '20px'
            }}>
              Passing the RSE exam opens doors to a rewarding career helping Canadian families and individuals build wealth. As a licensed retail securities advisor, you'll:
            </p>
            <ul style={{
              fontSize: '17px',
              lineHeight: 2,
              color: '#475569',
              paddingLeft: '24px'
            }}>
              <li><strong>Build Client Relationships:</strong> Develop long-term partnerships with retail investors across Canada</li>
              <li><strong>Provide Investment Advice:</strong> Recommend suitable securities, mutual funds, and managed products</li>
              <li><strong>Earn Competitive Compensation:</strong> Base salary plus commissions and bonuses based on performance</li>
              <li><strong>Career Growth:</strong> Progress to senior advisor, portfolio manager, or branch manager roles</li>
              <li><strong>Professional Recognition:</strong> Join a respected profession with strong regulatory standards</li>
            </ul>
          </section>

          {/* Canadian Context */}
          <section style={{ marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '24px'
            }}>
              Built for the Canadian Retail Securities Industry
            </h2>
            <p style={{
              fontSize: '18px',
              lineHeight: 1.8,
              color: '#475569',
              marginBottom: '16px'
            }}>
              This guide covers all Canadian-specific regulations and requirements:
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '24px'
            }}>
              {[
                'CIRO (formerly IIROC) regulations',
                'Provincial securities commissions',
                'Canadian Investor Protection Fund (CIPF)',
                'FINTRAC AML/ATF requirements',
                'NI 31-103 registration rules',
                'Canadian tax implications',
                'UMIR market integrity rules',
                'Quebec-specific requirements (AMF)'
              ].map((item, idx) => (
                <div key={idx} style={{
                  background: '#f8fafc',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  color: '#334155',
                  fontWeight: 600
                }}>
                  ✓ {item}
                </div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            borderRadius: '20px',
            padding: '48px 40px',
            textAlign: 'center',
            color: 'white'
          }}>
            <h2 style={{
              fontSize: '36px',
              fontWeight: 900,
              marginBottom: '16px'
            }}>
              Start Your RSE Exam Prep Today
            </h2>
            <p style={{
              fontSize: '20px',
              opacity: 0.95,
              marginBottom: '32px',
              maxWidth: '600px',
              margin: '0 auto 32px'
            }}>
              Join Canadian investment professionals who've used interactive learning to pass their RSE exam and launch their advisory careers
            </p>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              alignItems: 'center'
            }}>
              <Link
                to="/books/rse-interactive"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '18px 40px',
                  background: '#D4AF37',
                  color: 'white',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  fontSize: '20px',
                  fontWeight: 700,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                }}
              >
                <BookOpen size={24} />
                Get Instant Access - $49.99 CAD (Regular: $129.99)
              </Link>
              <p style={{
                fontSize: '15px',
                opacity: 0.9
              }}>
                One-time payment • Lifetime access • All updates included
              </p>
            </div>
          </section>

          {/* FAQ Section */}
          <section style={{ marginTop: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '24px'
            }}>
              Frequently Asked Questions
            </h2>
            
            {[
              {
                q: 'Is this guide approved by CIRO?',
                a: 'This is an independent study guide created by exam prep experts. While not officially endorsed by CIRO, it comprehensively covers all RSE exam content based on the official CIRO syllabus.'
              },
              {
                q: 'How long do I have access?',
                a: 'Lifetime access! Pay once ($49.99 CAD (Regular: $129.99)) and access all 9 elements, games, quizzes, and the mock exam forever. All future updates are included at no extra cost.'
              },
              {
                q: 'What\'s the difference between RSE and CIRE?',
                a: 'The RSE is more comprehensive than CIRE and designed for professionals advising retail clients on a broader range of securities. CIRE is for entry-level representatives with more limited product scope.'
              },
              {
                q: 'Can I use this if I\'m in Quebec?',
                a: 'Yes! The guide covers all Canadian provinces including Quebec-specific requirements like AMF regulations and the Chambre de la sécurité financière.'
              },
              {
                q: 'How is this different from traditional study materials?',
                a: 'Unlike passive reading materials, this guide uses interactive games, flashcards, and immediate feedback to boost retention. The gamified approach makes complex securities concepts easier to understand and remember.'
              }
            ].map((faq, idx) => (
              <div key={idx} style={{
                marginBottom: '24px',
                padding: '24px',
                background: '#f8fafc',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#1e293b',
                  marginBottom: '12px'
                }}>
                  {faq.q}
                </h3>
                <p style={{
                  fontSize: '16px',
                  lineHeight: 1.7,
                  color: '#64748b',
                  margin: 0
                }}>
                  {faq.a}
                </p>
              </div>
            ))}
          </section>

          {/* Related Resources Section */}
          <section style={{ marginTop: '60px', marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '32px',
              textAlign: 'center'
            }}>
              More RSE Exam Preparation Resources
            </h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '24px',
              marginBottom: '40px'
            }}>
              {/* RSE Course */}
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '32px',
                border: '2px solid #e2e8f0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 700,
                  display: 'inline-block',
                  marginBottom: '16px'
                }}>
                  ONLINE COURSE
                </div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#1e293b',
                  marginBottom: '12px'
                }}>
                  RSE Complete Course
                </h3>
                <p style={{
                  fontSize: '15px',
                  lineHeight: 1.7,
                  color: '#64748b',
                  marginBottom: '20px'
                }}>
                  Instructor-led videos, podcasts, infographics, mock exams, and comprehensive question bank. Monthly access.
                </p>
                <a
                  href="https://vittafinance.mykajabi.com/offers/2kzQbJim"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    padding: '14px',
                    background: '#1e3a8a',
                    color: 'white',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    fontSize: '16px',
                    fontWeight: 700
                  }}
                >
                  View Course - $29.99/mo
                </a>
              </div>

              {/* Free Study Guide */}
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '32px',
                border: '2px solid #10b981',
                boxShadow: '0 4px 12px rgba(16,185,129,0.15)'
              }}>
                <div style={{
                  background: '#10b981',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 700,
                  display: 'inline-block',
                  marginBottom: '16px'
                }}>
                  FREE RESOURCE
                </div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#1e293b',
                  marginBottom: '12px'
                }}>
                  Free RSE Study Guide
                </h3>
                <p style={{
                  fontSize: '15px',
                  lineHeight: 1.7,
                  color: '#64748b',
                  marginBottom: '20px'
                }}>
                  11-phase structured learning path with timelines, topics, and study strategies. 6-8 weeks preparation plan.
                </p>
                <Link
                  to="/rse-study-guide"
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    padding: '14px',
                    background: '#10b981',
                    color: 'white',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    fontSize: '16px',
                    fontWeight: 700
                  }}
                >
                  Get Free Study Guide
                </Link>
              </div>

              {/* Printed Books */}
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '32px',
                border: '2px solid #e2e8f0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
              }}>
                <div style={{
                  background: '#f59e0b',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 700,
                  display: 'inline-block',
                  marginBottom: '16px'
                }}>
                  PRINTED BOOKS
                </div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#1e293b',
                  marginBottom: '12px'
                }}>
                  RSE Study Materials
                </h3>
                <p style={{
                  fontSize: '15px',
                  lineHeight: 1.7,
                  color: '#64748b',
                  marginBottom: '20px'
                }}>
                  Comprehensive 780-page study guide and 5 full-length mock exams (600+ questions) available on Amazon.
                </p>
                <Link
                  to="/books"
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    padding: '14px',
                    background: '#f59e0b',
                    color: 'white',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    fontSize: '16px',
                    fontWeight: 700
                  }}
                >
                  View All Books
                </Link>
              </div>
            </div>

            {/* Other Interactive Guides */}
            <div style={{
              background: '#f0f9ff',
              borderRadius: '16px',
              padding: '32px',
              border: '2px solid #3b82f6'
            }}>
              <h3 style={{
                fontSize: '22px',
                fontWeight: 700,
                color: '#1e40af',
                marginBottom: '16px',
                textAlign: 'center'
              }}>
                Explore Other Interactive Study Guides
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginTop: '24px'
              }}>
                <Link
                  to="/books/cire-interactive"
                  style={{
                    padding: '20px',
                    background: 'white',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    textAlign: 'center',
                    border: '1px solid #e2e8f0',
                    transition: 'transform 0.2s'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>📘</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>CIRE Interactive</div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>$34.99 CAD</div>
                </Link>
                <Link
                  to="/books/derivatives-interactive"
                  style={{
                    padding: '20px',
                    background: 'white',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    textAlign: 'center',
                    border: '1px solid #e2e8f0',
                    transition: 'transform 0.2s'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>📊</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>Derivatives Interactive</div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>$49.99 CAD</div>
                </Link>
                <Link
                  to="/books/ise-interactive"
                  style={{
                    padding: '20px',
                    background: 'white',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    textAlign: 'center',
                    border: '1px solid #e2e8f0',
                    transition: 'transform 0.2s'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>🏢</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>ISE Interactive</div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>$49.99 CAD</div>
                </Link>
              </div>
            </div>
          </section>
        </div>
      </article>
    </>
  );
};

export default RSEInteractiveGuideBlog;